import {BIN_RULES, OPPORTUNITY_RULES} from './reviewRules';
import { ShieldAlert } from 'lucide-react';
import { Workflow } from 'lucide-react';
import {
  Archive,
  ArrowLeftRight,
  BadgeCheck,
  BookOpen,
  CircleDot,
  ClipboardList,
  Cog,
  FileBarChart,
  FileInput,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  Gauge,
  IdCard,
  Landmark,
  BarChart3,
  ScanLine,
  MapPin,
  Building2,
  Layers,
  LifeBuoy,
  type LucideIcon,
  PackageOpen,
  ReceiptText,
  Scale,
  Settings,
  ShoppingCart,
  TrendingUp,
  Truck,
  UserCog,
  Users,
  Warehouse,
} from "lucide-react";
import { canAccess, canReadResource, hasPermission, landingPathFor, type Permission, type Role, type UserAccess } from "@hisense/shared";

export interface NavItem {
  key: string; // i18n key under nav.*
  to: string;
  icon: LucideIcon;
  comingSoon?: boolean;
  /**
   * Ẩn khỏi sidebar, tìm kiếm nhanh và trang đầu — nhưng VẪN nằm trong `NAV_FLAT`
   * để đường dẫn còn được gác đúng bảng tick và danh mục menu của máy chủ không
   * lệch. Khác `comingSoon` (hiện kèm nhãn): ẩn là không thấy gì cả.
   */
  hidden?: boolean;
  reviewRuleIds?: readonly string[];
  /** Quyền bắt buộc để THẤY mục này. Bỏ trống → dùng `resource`. */
  perm?: Permission;
  /**
   * Tên tài nguyên phía API, để áp đúng giới hạn của kế toán
   * (`requireResourceRead`). Bỏ cả hai → chỉ cần `records:read`.
   */
  resource?: string;
}

export interface NavGroup {
  key: string; // i18n key under nav.*
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    key: "overview",
    items: [
      { key: "dashboard", to: "/", icon: Gauge, perm: "dashboard:view" },
      { key: "analytics", to: "/analytics", icon: TrendingUp, perm: "analytics:view" },
      { key: "profit", to: "/profit", icon: Scale, perm: "supplier_cost:view" },
      // Không dùng chữ Lợi nhuận/Giá trên menu: người vào đây là người không được xem giá.
      { key: "piStatus", to: "/pi-status", icon: ClipboardList, perm: "records:read" },
      // Quy trình thường (29/10/2026). FLOW-R01 vẫn chờ khách xác nhận nhưng KHÔNG gắn nhãn — anh Toàn tự hỏi khách (reviewRules.ts).
      { key: "orderProcess", to: "/order-process", icon: Workflow, resource: "order-process" },
      // 16/09/2026: KPI vận hành chốt theo chuẩn, không có tiền → nhân viên nội bộ đều xem (kế toán không).
      { key: "kpis", to: "/kpis", icon: BarChart3, resource: "kpis" },
    ],
  },
  {
    key: "masterData",
    items: [
      { key: "products", to: "/products", icon: CircleDot, resource: "products" },
      { key: "customers", to: "/customers", icon: Users, resource: "customers" },
      { key: "suppliers", to: "/suppliers", icon: Truck, resource: "suppliers" },
      { key:"unitPricing",to:"/unit-pricing",icon:Scale,perm:"supplier_cost:view" }, // UOM-R01..R03 chốt 19/09/2026 — reviewRules.ts
    ],
  },
  {
    key: "samples",
    items: [
      { key: "sampleOrders", to: "/sample-orders", icon: FlaskConical, resource: "sample-orders" },
      { key: "sampleCards", to: "/sample-cards", icon: IdCard, resource: "sample-cards" },
    ],
  },
  {
    key: "sales",
    items: [
      { key: "quotations", to: "/quotations", icon: FileSpreadsheet, resource: "quotations" },
      // Anh Toàn 16/09/2026: "ẩn đi, hiện giờ không cần thiết". Route và API giữ
      // nguyên (đơn bán hàng vẫn chọn được nguồn từ cơ hội), chỉ không còn lối vào.
      { key: "opportunities", to: "/sales-orders/opportunities", icon: ClipboardList, perm: "supplier_cost:view", reviewRuleIds:Object.values(OPPORTUNITY_RULES), hidden: true },
      // 19/09/2026: năm quy tắc Đơn bán hàng (SO-R01..R03, SO-WAVE-R01) đã chốt theo chuẩn ERP — không còn nhãn chờ khách xác nhận.
      { key: "salesOrders", to: "/sales-orders", icon: ClipboardList, perm: "supplier_cost:view" },
      { key: "proformaInvoices", to: "/proforma-invoices", icon: FileText, resource: "proforma-invoices" },
      { key: "invoices", to: "/invoices", icon: ReceiptText, resource: "invoices" },
      // 19/09/2026: Hậu mãi đã chốt TKT-R01, TKT-R02 theo chuẩn — không còn nhãn chờ khách xác nhận.
      { key: "serviceTickets", to: "/service-tickets", icon: LifeBuoy, resource: "service-tickets" },
    ],
  },
  {
    key: "procurement",
    items: [
      // 19/09/2026: MRP-R01..R03 đã chốt theo chuẩn — không còn nhãn chờ khách xác nhận.
      {key:"mrp",to:"/mrp",icon:Layers,perm:"supplier_cost:view"},
      // 19/09/2026: PR-R01..R03 đã chốt theo chuẩn — không còn nhãn chờ khách xác nhận.
      { key: "requisitions", to: "/purchase-requisitions", icon: FileInput, perm: "supplier_cost:view" },
      // 19/09/2026: SOURCING-R01..R03 đã chốt theo chuẩn — không còn nhãn chờ khách xác nhận.
      { key:"sourcing",to:"/sourcing",icon:FileSpreadsheet,perm:"supplier_cost:view" },
      { key: "purchaseOrders", to: "/purchase-orders", icon: ShoppingCart, perm: "supplier_cost:view" },
      // 20/09/2026: SCL-R01, SCL-R02 đã chốt theo chuẩn — không còn nhãn chờ khách xác nhận.
      { key: "supplierClaims", to: "/supplier-claims", icon: ShieldAlert, perm: "supplier_cost:view" },
      // 18/09/2026 chuyển từ "Tổng quan & Báo cáo": cổng là nơi làm việc với nhà cung cấp (đơn mua,
      // phiếu nhập, công nợ, khiếu nại), không phải báo cáo — như SAP/Odoo đặt trong mua hàng.
      // Khách chốt 15/09/2026: mở cổng cho nhà cung cấp, đăng nhập ngay ở trang đăng nhập chính.
      { key: "portal", to: "/portal", icon: Building2, perm: "supplier_cost:view" },
    ],
  },
  {
    key: "inventoryDelivery",
    items: [
      // 20/09/2026: W-R01…W-R09 đã chốt theo chuẩn — không còn nhãn chờ khách xác nhận.
      { key: "warehouse", to: "/warehouse", icon: Warehouse, resource: "warehouses" },
      // 30/10/2026 (YC-07 phần 1): tính năng đang làm — dấu "Đang làm" và mật khẩu lấy từ PREVIEW_FEATURES (featurePreview.ts).
      { key: "warehouseLocations", to: "/warehouse-locations", icon: MapPin, resource: "warehouses", reviewRuleIds: Object.values(BIN_RULES) },
      // 20/09/2026: MOVE-R01…R03, RSV-R01…R03, CNT-R01/R02, OPEN-R01, SALE-R01, RMA-R01 đã chốt theo chuẩn — không còn nhãn chờ khách xác nhận.
      { key: "stockMoves", to: "/stock-moves", icon: ArrowLeftRight, perm: "supplier_cost:view" },
      { key: "packingLists", to: "/packing-lists", icon: PackageOpen, resource: "packing-lists" },
      { key: "deliveryNotes", to: "/delivery-notes", icon: Truck, resource: "delivery-notes" },
      // 18/09/2026 chuyển từ "Tổng quan & Báo cáo": máy quét và in tem LOT là công cụ của kho.
      // DEV-R01, DEV-R02 chốt 20/09/2026 cùng hai quy tắc mới (work, label) — xem components/scan/ScanRules.tsx.
      { key: "devices", to: "/devices", icon: ScanLine, perm: "supplier_cost:view" },
    ],
  },
  {
    key: "manufacturingQuality",
    items: [
      // BOM-R01…R03 chốt 20/09/2026, thêm hai quy tắc mới (không làm bản riêng theo khách; ai được làm gì) — xem components/production/BomRules.tsx.
      { key: "boms", to: "/boms", icon: Layers, perm: "supplier_cost:view" },
      // ROUTE-R01…R04 chốt 20/09/2026, thêm quy tắc công đoạn thuê ngoài — xem components/production/RoutingRules.tsx.
      { key:"routings",to:"/routings",icon:Workflow,perm:"supplier_cost:view" },
      // PRODUCTION-R01…R03, MATERIAL-R01…R03, EXEC-R01…R05 chốt 20/09/2026 — xem components/production/ProductionRules.tsx.
      // SUB-R01…R04 (gia công ngoài) chốt cùng đợt, gộp vào quy tắc "subcontract" của màn Thực thi.
      { key: "production", to: "/production", icon: Cog, perm: "supplier_cost:view" },
      // IQC-R01…R05, SPEC-R01…R03 và NCR-R01…R03 chốt 20/09/2026 — xem components/quality/QualityRules.tsx.
      { key: "quality", to: "/quality", icon: BadgeCheck, perm: "supplier_cost:view" },
      { key: "pProcess", to: "/p-process", icon: Workflow, resource: "p-process" },
    ],
  },
  {
    key: "financeReceivables",
    items: [
      { key: "statement", to: "/statement", icon: FileBarChart, resource: "statements" },
      // GL-R01…R02, AP-R01, COST-R01 chốt 20/09/2026 — xem components/finance/FinanceRules.tsx.
      // Phần còn thiếu là DỮ LIỆU kế toán của Hisense (hệ thống tài khoản, mã thuế, dung sai, tỷ lệ giá
      // thành), không phải quy tắc — nói thẳng trong manual thay vì giữ một nhãn vàng.
      { key: "finance", to: "/finance", icon: Landmark, resource: "journal-entries" },
      { key: "glAccounts", to: "/gl-accounts", icon: BookOpen, resource: "gl-accounts", comingSoon: true },
    ],
  },
  {
    key: "admin",
    items: [
      { key: "users", to: "/users", icon: UserCog, perm: "users:manage" },
      { key: "settings", to: "/settings", icon: Settings, perm: "settings:manage" },
      { key: "deletedRecords", to: "/deleted-records", icon: Archive, perm: "deleted:manage" },
    ],
  },
];

/**
 * Một mục có hiện với vai trò này không.
 *
 * Đây là tiện ích hiển thị, KHÔNG phải hàng rào an ninh — máy chủ vẫn gác độc
 * lập ở mọi route. Ẩn mục menu chỉ để người dùng khỏi đâm vào tường 403.
 */
export function canSeeNavItem(role: Role, item: NavItem, access?: UserAccess): boolean {
  if (item.hidden) return false;
  // Tài khoản có bảng quyền riêng: đọc ĐÚNG bảng mà máy chủ đang chặn theo,
  // để không bao giờ vẽ ra một mục bấm vào là 403.
  if (access && access.mode === "custom") return canAccess(access, item.key, "view");
  if (item.perm) return hasPermission(role, item.perm);
  if (item.resource) return canReadResource(role, item.resource);
  return hasPermission(role, "records:read");
}

/** Menu đã lọc theo vai trò; nhóm rỗng bị bỏ hẳn thay vì hiện tiêu đề trơ trọi. */
export function navFor(role: Role, portalSupplierId?: string | null, access?: UserAccess): NavGroup[] {
  if (portalSupplierId) {
    const items = NAV_FLAT.filter((i) => i.key === "portal");
    return items.length ? [{ key: "overview", items }] : [];
  }
  return NAV.map((g) => ({ ...g, items: g.items.filter((i) => canSeeNavItem(role, i, access)) }))
    .filter((g) => g.items.length > 0);
}

/** Flat list for the command palette. */
export const NAV_FLAT: NavItem[] = NAV.flatMap((g) => g.items);

/** Command palette cũng phải lọc — nếu không nó là cửa sau vào đúng những
 *  trang vừa bị ẩn khỏi sidebar. */
export function navFlatFor(role: Role, portalSupplierId?: string | null, access?: UserAccess): NavItem[] {
  if (portalSupplierId) return NAV_FLAT.filter((i) => i.key === "portal");
  return NAV_FLAT.filter((i) => canSeeNavItem(role, i, access));
}

/**
 * Mục menu phụ trách một đường dẫn trên web (khớp tiền tố dài nhất).
 *
 * Chốt chặn route dùng nó để hỏi ĐÚNG bảng tick mà máy chủ đang chặn theo,
 * thay vì đoán lại từ vai trò.
 */
const BY_PATH = [...NAV_FLAT].sort((a, b) => b.to.length - a.to.length);

export function menuKeyForPath(pathname: string): string | undefined {
  return BY_PATH.find((i) => i.to === pathname || (i.to !== "/" && pathname.startsWith(`${i.to}/`)))?.key
    ?? (pathname === "/" ? "dashboard" : undefined);
}

/**
 * Trang đầu tiên tài khoản này thực sự mở được.
 *
 * Tài khoản có bảng quyền riêng thì "/" (Dashboard) thường không nằm trong đó,
 * nên phải lấy mục đầu tiên của menu đã lọc — nếu không họ đăng nhập xong là
 * rơi thẳng vào một trang báo lỗi, đúng như đã thấy khi thử.
 */
export function landingFor(role: Role, portalSupplierId?: string | null, access?: UserAccess): string {
  if (portalSupplierId) return "/portal";
  if (access?.mode === "custom") {
    const first = navFor(role, null, access)[0]?.items[0]?.to;
    return first ?? "/settings";
  }
  return landingPathFor(role, portalSupplierId);
}

